import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { AuthUser } from "../../auth/shared/types/auth";
import { getChallengeById } from "../../challenges/services/challengeAdminService";
import type { ChallengeDetail } from "../../challenges/types/challengeAdmin";
import type {
  ChallengeEventDraft,
  ChallengeModalMode,
  DraftPair,
  DraftPlayer,
  DraftSideChoice,
  PairMode,
} from "../../challenges/types/challengeDraft";
import { sideChoiceToDraftSide } from "../../challenges/utils/challengePairs";
import { getEventPaymentSettings } from "../services/asaasAdminService";
import {
  getEventChallengeLinkSummary,
  listEventChallengeLinks,
  type AdminEventChallengeLink,
} from "../services/eventRegistrationAdminService";
import {
  isFixedPairOnlyChallengeType,
  limitPairsForChallengeType,
  normalizeChallengeDetail,
  parseAmountToCents,
  resolveChallengeEventId,
} from "../utils/adminChallengeEditor";
import {
  normalizeTournamentRegistrationCategoriesForEdit,
  normalizeTournamentShirtSizes,
} from "../utils/tournamentCategories";
import { toDateTimeLocal } from "../utils/tournamentOperationSchedule";
import { formatAvailabilityPeriodLines } from "../../tournamentAvailability/tournamentAvailabilitySettings";

function normalizeCentsLike(value: unknown): number | null {
  if (value === undefined || value === null || value === "") return null;

  if (typeof value === "number") {
    const cents = Math.round(value);
    return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const normalized = raw.replace(/\s+/g, "").replace(/[^\d,.-]/g, "");
  const unsigned = normalized.replace(/-/g, "");
  const lastCommaIndex = unsigned.lastIndexOf(",");
  const lastDotIndex = unsigned.lastIndexOf(".");
  const decimalSeparator =
    lastCommaIndex >= 0 && lastDotIndex >= 0
      ? lastCommaIndex > lastDotIndex
        ? ","
        : "."
      : lastCommaIndex >= 0
        ? ","
        : lastDotIndex >= 0
          ? "."
          : "";

  let cents: number;
  if (decimalSeparator) {
    const separatorIndex = unsigned.lastIndexOf(decimalSeparator);
    const integerPart = unsigned.slice(0, separatorIndex).replace(/\D/g, "");
    const fractionPart = unsigned.slice(separatorIndex + 1).replace(/\D/g, "");
    const separatorLooksDecimal =
      fractionPart.length > 0 && fractionPart.length <= 2;

    if (separatorLooksDecimal) {
      cents = Math.round(Number(`${integerPart || "0"}.${fractionPart}`));
    } else {
      cents = Number(unsigned.replace(/\D/g, ""));
    }
  } else {
    const digits = unsigned.replace(/\D/g, "");
    if (!digits) return null;
    cents = Number(digits);
  }

  if (!Number.isFinite(cents)) return null;
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

function normalizeAmountLikeToCents(value: unknown): number | null {
  const cents = parseAmountToCents(String(value ?? ""));
  return Number.isSafeInteger(cents) && Number(cents) > 0
    ? Number(cents)
    : null;
}

export function resolveSavedAmountCents(
  ...sources: Array<object | null | undefined>
) {
  const keys: Array<{ name: string; unit: "cents" | "amount" }> = [
    { name: "amountPerPlayerCents", unit: "cents" },
    { name: "amountCents", unit: "cents" },
    { name: "price", unit: "amount" },
    { name: "value", unit: "amount" },
    { name: "amount", unit: "amount" },
    { name: "registrationFee", unit: "amount" },
    { name: "entryFee", unit: "amount" },
    { name: "categoryPrice", unit: "amount" },
    { name: "valorInscricao", unit: "amount" },
    { name: "valor", unit: "amount" },
    { name: "preco", unit: "amount" },
  ];
  for (const source of sources) {
    if (!source) continue;
    const indexedSource = source as Record<string, unknown>;
    for (const key of keys) {
      const cents =
        key.unit === "cents"
          ? normalizeCentsLike(indexedSource[key.name])
          : normalizeAmountLikeToCents(indexedSource[key.name]);
      if (cents !== null) return cents;
    }
  }
  return null;
}

type UseAdminChallengeLoaderInput = {
  eventChallengeLinks: Record<string, AdminEventChallengeLink>;
  isAdmin: boolean;
  isOrganizer: boolean;
  loadUsers: () => Promise<void>;
  setActiveTournamentCategoryKey: Dispatch<SetStateAction<string>>;
  setChallengeDetail: Dispatch<SetStateAction<ChallengeDetail | null>>;
  setChallengeEventDraft: Dispatch<SetStateAction<ChallengeEventDraft>>;
  setChallengeModalMode: Dispatch<SetStateAction<ChallengeModalMode>>;
  setChallengeRoundsJson: Dispatch<SetStateAction<string>>;
  setDraftPairs: Dispatch<SetStateAction<DraftPair[]>>;
  setDraftPlayers: Dispatch<SetStateAction<DraftPlayer[]>>;
  setDrawCpf: Dispatch<SetStateAction<string>>;
  setDrawSideChoice: Dispatch<SetStateAction<DraftSideChoice>>;
  setEventChallengeLinkDrafts: Dispatch<SetStateAction<Record<string, string>>>;
  setEventChallengeLinks: Dispatch<
    SetStateAction<Record<string, AdminEventChallengeLink>>
  >;
  setPairCpf1: Dispatch<SetStateAction<string>>;
  setPairCpf2: Dispatch<SetStateAction<string>>;
  setPairMode: Dispatch<SetStateAction<PairMode>>;
  setSelectedChallengeId: Dispatch<SetStateAction<number | null>>;
  setShowChallengeModal: Dispatch<SetStateAction<boolean>>;
  showErrorToast: (message: string) => void;
  token?: string | null;
  tournamentDraftCategoryKeyRef: MutableRefObject<string>;
  users: AuthUser[];
};

export function useAdminChallengeLoader({
  eventChallengeLinks,
  isAdmin,
  isOrganizer,
  loadUsers,
  setActiveTournamentCategoryKey,
  setChallengeDetail,
  setChallengeEventDraft,
  setChallengeModalMode,
  setChallengeRoundsJson,
  setDraftPairs,
  setDraftPlayers,
  setDrawCpf,
  setDrawSideChoice,
  setEventChallengeLinkDrafts,
  setEventChallengeLinks,
  setPairCpf1,
  setPairCpf2,
  setPairMode,
  setSelectedChallengeId,
  setShowChallengeModal,
  showErrorToast,
  token,
  tournamentDraftCategoryKeyRef,
  users,
}: UseAdminChallengeLoaderInput) {
  return useCallback(
    async (id: number, routeMode?: ChallengeModalMode) => {
      if (!token) return false;
      setSelectedChallengeId(id);
      try {
        if ((isAdmin || isOrganizer) && users.length === 0) {
          await loadUsers();
        }
        const detail = await getChallengeById(token, id);
        const normalized = normalizeChallengeDetail(detail);
        const loadedDraftPairs = normalized.pairs.map((pair) => ({
          id: pair.id,
          cpf1: String(pair.cpf1 || "").trim(),
          cpf2: String(pair.cpf2 || "").trim(),
          label: pair.label,
        }));
        const customPocoPairLimit =
          isFixedPairOnlyChallengeType(normalized.challengeType)
            ? Math.floor(
                Number(
                  normalized.playersCount ||
                    (Number(normalized.courtsCount) === 4 ? 16 : 12),
                ) / 2,
              )
            : null;
        const limitedDraftPairs =
          customPocoPairLimit !== null
            ? loadedDraftPairs.slice(0, customPocoPairLimit)
            : limitPairsForChallengeType(
                loadedDraftPairs,
                normalized.challengeType,
              );
        const normalizedWithCap =
          limitedDraftPairs.length === loadedDraftPairs.length
            ? normalized
            : {
                ...normalized,
                participantCpfs: Array.from(
                  new Set(
                    limitedDraftPairs.flatMap((pair) => [pair.cpf1, pair.cpf2]),
                  ),
                ),
              };
        const isLoadedTournament =
          String(normalizedWithCap.challengeType || "")
            .trim()
            .toLowerCase() === "tournament";
        const isLoadedCategorizedChallenge =
          ["challenge", "poco"].includes(
            String(normalizedWithCap.challengeType || "")
              .trim()
              .toLowerCase(),
          ) &&
          Array.isArray(normalizedWithCap.registrationCategories) &&
          normalizedWithCap.registrationCategories.length > 0;
        const hasLoadedRegistrationCategories =
          isLoadedTournament || isLoadedCategorizedChallenge;
        const loadedTournamentCategories = hasLoadedRegistrationCategories
          ? normalizeTournamentRegistrationCategoriesForEdit(
              normalizedWithCap.registrationCategories,
            )
          : [];
        const initialTournamentCategoryKey = String(
          loadedTournamentCategories[0]?.categoryKey || "",
        ).trim();
        const detailForEditor = hasLoadedRegistrationCategories
          ? { ...normalizedWithCap, participantCpfs: [] }
          : normalizedWithCap;
        setChallengeDetail(detailForEditor);
        const normalizedRounds = Array.isArray(detailForEditor.rounds)
          ? detailForEditor.rounds
          : [];
        setChallengeRoundsJson(JSON.stringify(normalizedRounds, null, 2));
        if (hasLoadedRegistrationCategories) {
          const firstType = String(
            loadedTournamentCategories[0]?.registrationType || "",
          )
            .trim()
            .toLowerCase();
          setPairMode(firstType === "fixed_pair" ? "fixed" : "draw");
          tournamentDraftCategoryKeyRef.current = initialTournamentCategoryKey;
          setActiveTournamentCategoryKey(initialTournamentCategoryKey);
        } else if (normalizedWithCap.challengeType === "super8") {
          setPairMode("draw");
          tournamentDraftCategoryKeyRef.current = "";
          setActiveTournamentCategoryKey("");
        } else {
          setPairMode("fixed");
          tournamentDraftCategoryKeyRef.current = "";
          setActiveTournamentCategoryKey("");
        }
        setPairCpf1("");
        setPairCpf2("");
        setDraftPairs(hasLoadedRegistrationCategories ? [] : limitedDraftPairs);
        if (
          !hasLoadedRegistrationCategories &&
          limitedDraftPairs.length < loadedDraftPairs.length
        ) {
          const pairLimit = customPocoPairLimit ?? 6;
          showErrorToast(
            `Este desafio permite no máximo ${pairLimit} duplas (${pairLimit * 2} participantes).`,
          );
        }
        setDrawCpf("");
        setDrawSideChoice("direita");
        setDraftPlayers(
          isLoadedTournament
            ? []
            : normalized.participants.map((participant) => ({
                cpf: String(participant.cpf || "").trim(),
                name:
                  String(participant.name || "").trim() ||
                  siteTexts.challengeEngine.labels.player,
                side: sideChoiceToDraftSide(participant.preferredSide, {
                  preferenceMissing: !participant.preferredSide,
                }),
              })),
        );
        setChallengeModalMode(
          isLoadedTournament
            ? "edit"
            : routeMode || "edit",
        );

        const fallbackEventId = resolveChallengeEventId(normalized);
        let links = Object.values(eventChallengeLinks);
        if (
          !links.some(
            (entry) => Number(entry.tournamentId) === Number(normalized.id),
          )
        ) {
          try {
            links = await listEventChallengeLinks(token);
            const nextMap: Record<string, AdminEventChallengeLink> = {};
            const nextDrafts: Record<string, string> = {};
            links.forEach((link) => {
              nextMap[link.eventId] = link;
              nextDrafts[link.eventId] = String(link.tournamentId);
            });
            setEventChallengeLinks(nextMap);
            setEventChallengeLinkDrafts((prev) => ({ ...prev, ...nextDrafts }));
          } catch (_err) {
            // Se falhar, segue com o que já existe localmente.
          }
        }

        const linkedEntry = links.find(
          (entry) => Number(entry.tournamentId) === Number(normalized.id),
        );
        const linkedEventId = String(
          linkedEntry?.eventId || fallbackEventId,
        ).trim();
        let amountPerPlayerCents: number | null = null;
        let registrationType: "individual" | "fixed_pair" = "individual";
        if (isFixedPairOnlyChallengeType(normalized.challengeType)) {
          registrationType = "fixed_pair";
        }
        let registrationsOpen = false;
        let linkSummary: Awaited<
          ReturnType<typeof getEventChallengeLinkSummary>
        > | null = null;

        if (linkedEventId && isAdmin) {
          try {
            const paymentSettings = await getEventPaymentSettings(
              token,
              linkedEventId,
            );
            amountPerPlayerCents = resolveSavedAmountCents(paymentSettings);
            const amountMode = String(paymentSettings?.amountMode || "")
              .trim()
              .toLowerCase();
            if (isFixedPairOnlyChallengeType(normalized.challengeType)) {
              registrationType = "fixed_pair";
            } else if (amountMode === "pair" || amountMode === "fixed_pair") {
              registrationType = "fixed_pair";
            } else if (amountMode === "individual") {
              registrationType = "individual";
            }
            registrationsOpen = Boolean(paymentSettings?.registrationsOpen);
          } catch (_err) {
            // Organizador pode não ter acesso; mantém fallback do conteúdo.
          }
          try {
            linkSummary = await getEventChallengeLinkSummary(linkedEventId);
          } catch (_err) {
            linkSummary = null;
          }
        } else if (linkedEventId && isOrganizer) {
          try {
            const summary = await getEventChallengeLinkSummary(linkedEventId);
            linkSummary = summary;
            amountPerPlayerCents = resolveSavedAmountCents(summary);
            registrationsOpen = Boolean(summary?.registrationsOpen);
            if (!isFixedPairOnlyChallengeType(normalized.challengeType)) {
              registrationType =
                String(summary?.registrationType || "")
                  .trim()
                  .toLowerCase() === "fixed_pair"
                  ? "fixed_pair"
                  : "individual";
            }
          } catch (_err) {
            // Mantém fallback do conteúdo se não conseguir carregar resumo do evento.
          }
        }

        if (amountPerPlayerCents === null) {
          amountPerPlayerCents = resolveSavedAmountCents(
            linkSummary,
            normalized,
            linkedEntry,
          );
        }

        const loadedShirtSizesEnabled = Boolean(normalized.shirtSizesEnabled);
        const loadedShirtAvailabilityType =
          linkSummary?.shirtAvailabilityType === "period" ||
          linkSummary?.shirtAvailabilityType === "quantity"
            ? linkSummary.shirtAvailabilityType
            : normalized.shirtAvailabilityType === "period" ||
                normalized.shirtAvailabilityType === "quantity"
              ? normalized.shirtAvailabilityType
              : "none";
        const loadedShirtDeadlineAt = toDateTimeLocal(
          linkSummary?.shirtDeadlineAt || normalized.shirtDeadlineAt || "",
        );

        setChallengeEventDraft({
          enabled: true,
          imageUrl: String(normalized.eventImageUrl || "").trim(),
          amountPerPlayer:
            typeof amountPerPlayerCents === "number"
              ? (amountPerPlayerCents / 100).toFixed(2)
              : "",
          savedAmountPerPlayerCents: amountPerPlayerCents,
          registrationType,
          registrationsOpen,
          savedShirtSizesEnabled: loadedShirtSizesEnabled,
          savedShirtAvailabilityType: loadedShirtAvailabilityType,
          savedShirtDeadlineAt: loadedShirtDeadlineAt || null,
          shirtSizesEnabled: loadedShirtSizesEnabled,
          shirtSizes: loadedShirtSizesEnabled
            ? normalizeTournamentShirtSizes(normalized.shirtSizes)
            : [],
          shirtAvailabilityType: loadedShirtAvailabilityType,
          shirtDeadlineAt: loadedShirtDeadlineAt,
          shirtQuantityLimit:
            (linkSummary?.shirtQuantityLimit ??
              normalized.shirtQuantityLimit) === undefined ||
            (linkSummary?.shirtQuantityLimit ??
              normalized.shirtQuantityLimit) === null
              ? ""
              : String(
                  linkSummary?.shirtQuantityLimit ??
                    normalized.shirtQuantityLimit,
                ),
          shirtConfirmedCount:
            linkSummary?.shirtConfirmedCount ??
            normalized.shirtConfirmedCount ??
            0,
          shirtAvailableCount:
            linkSummary?.shirtAvailableCount ??
            normalized.shirtAvailableCount ??
            null,
          availabilityRestrictionsEnabled: Boolean(
            normalized.availabilityRestrictionsEnabled,
          ),
          availabilityRestrictionAmount: (() => {
            const cents = normalizeCentsLike(
              normalized.availabilityRestrictionAmountCents,
            );
            return cents !== null ? (cents / 100).toFixed(2) : "";
          })(),
          availabilityRestrictionLimit:
            normalized.availabilityRestrictionLimit === undefined ||
            normalized.availabilityRestrictionLimit === null
              ? "1"
              : String(normalized.availabilityRestrictionLimit),
          availabilityRestrictionDeadlineAt: String(
            normalized.availabilityRestrictionDeadlineAt || "",
          ).slice(0, 16),
          availabilityRestrictionTypes:
            Array.isArray(normalized.availabilityRestrictionTypes) &&
            normalized.availabilityRestrictionTypes.length > 0
              ? normalized.availabilityRestrictionTypes
              : ["full_day", "day_period"],
          availabilityRestrictionPeriodsText: formatAvailabilityPeriodLines(
            normalized.availabilityRestrictionPeriods,
          ),
        });
        setShowChallengeModal(true);
        return true;
      } catch (_err) {
        setChallengeDetail(null);
        setChallengeRoundsJson("[]");
        showErrorToast(siteTexts.admin.toasts.challengeLoadError);
        return false;
      }
    },
    [
      eventChallengeLinks,
      isAdmin,
      isOrganizer,
      loadUsers,
      setActiveTournamentCategoryKey,
      setChallengeDetail,
      setChallengeEventDraft,
      setChallengeModalMode,
      setChallengeRoundsJson,
      setDraftPairs,
      setDraftPlayers,
      setDrawCpf,
      setDrawSideChoice,
      setEventChallengeLinkDrafts,
      setEventChallengeLinks,
      setPairCpf1,
      setPairCpf2,
      setPairMode,
      setSelectedChallengeId,
      setShowChallengeModal,
      showErrorToast,
      token,
      tournamentDraftCategoryKeyRef,
      users.length,
    ],
  );
}
